// src/pages/championships/KnockoutDrawAnimation.jsx
import React, { useState, useEffect, useMemo } from 'react';
import championshipService from '../../services/championshipService';
import './KnockoutDrawAnimation.css';

function KnockoutDrawAnimation({ championshipId, teams, phase, onDrawComplete, onCancel }) {
  const [availableTeams, setAvailableTeams] = useState([...teams]);
  const [pairings, setPairings] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!teams || teams.length === 0) {
      setError('Nenhum time disponível para o sorteio.');
    }
    if (teams.length % 2 !== 0) {
      setError('Número ímpar de times. Não é possível realizar o sorteio.');
    }
  }, [teams]);

  const knockoutSlots = useMemo(() => {
    const numMatches = teams.length / 2;
    return Array.from({ length: numMatches }, (_, i) => ({
      matchNumber: i + 1,
      teamA: null,
      teamB: null,
    }));
  }, [teams.length]);

  const findTeamByGroupAndPosition = (group, position) => {
    return availableTeams.find(t =>
      t.groupNameNormalized === group && t.position === position
    );
  };

  const startDraw = async () => {
    if (!teams || teams.length === 0) {
      setError('Nenhum time disponível para o sorteio.');
      return;
    }
    if (teams.length % 2 !== 0) {
      setError('Número ímpar de times. Não é possível realizar o sorteio.');
      return;
    }

    setIsDrawing(true);
    setError('');
    setSuccess('');
    setPairings([]);

    let generatedPairings = [];

    const isFromGroups = availableTeams.every(t => t.groupNameNormalized && t.position);

    if (isFromGroups) {
      if (availableTeams.length === 4) {
        generatedPairings = [
          { teamA: findTeamByGroupAndPosition('A', 1), teamB: findTeamByGroupAndPosition('B', 2) },
          { teamA: findTeamByGroupAndPosition('B', 1), teamB: findTeamByGroupAndPosition('A', 2) },
        ];
      } else if (availableTeams.length === 8) {
        generatedPairings = [
          { teamA: findTeamByGroupAndPosition('A', 1), teamB: findTeamByGroupAndPosition('B', 2) },
          { teamA: findTeamByGroupAndPosition('C', 1), teamB: findTeamByGroupAndPosition('D', 2) },
          { teamA: findTeamByGroupAndPosition('B', 1), teamB: findTeamByGroupAndPosition('A', 2) },
          { teamA: findTeamByGroupAndPosition('D', 1), teamB: findTeamByGroupAndPosition('C', 2) },
        ];
      } else if (availableTeams.length === 16) {
        const winnerA = findTeamByGroupAndPosition('A', 1);
        const winnerH = findTeamByGroupAndPosition('H', 1);

        if (winnerA && winnerH) {
          console.log("Detectado formato de 8 grupos para Oitavas.");
          const winners = {
            A: findTeamByGroupAndPosition('A', 1), B: findTeamByGroupAndPosition('B', 1),
            C: findTeamByGroupAndPosition('C', 1), D: findTeamByGroupAndPosition('D', 1),
            E: findTeamByGroupAndPosition('E', 1), F: findTeamByGroupAndPosition('F', 1),
            G: findTeamByGroupAndPosition('G', 1), H: findTeamByGroupAndPosition('H', 1),
          };
          const runnersUp = {
            A: findTeamByGroupAndPosition('A', 2), B: findTeamByGroupAndPosition('B', 2),
            C: findTeamByGroupAndPosition('C', 2), D: findTeamByGroupAndPosition('D', 2),
            E: findTeamByGroupAndPosition('E', 2), F: findTeamByGroupAndPosition('F', 2),
            G: findTeamByGroupAndPosition('G', 2), H: findTeamByGroupAndPosition('H', 2),
          };
          generatedPairings = [
            { teamA: winners.A, teamB: runnersUp.B },
            { teamA: winners.C, teamB: runnersUp.D },
            { teamA: winners.E, teamB: runnersUp.F },
            { teamA: winners.G, teamB: runnersUp.H },
            { teamA: winners.B, teamB: runnersUp.A },
            { teamA: winners.D, teamB: runnersUp.C },
            { teamA: winners.F, teamB: runnersUp.E },
            { teamA: winners.H, teamB: runnersUp.G },
          ];
        } else {
          console.log("Formato de grupos alternativo detectado. Realizando sorteio aleatório.");
          let shuffled = [...availableTeams].sort(() => 0.5 - Math.random());
          for (let i = 0; i < shuffled.length; i += 2) {
            generatedPairings.push({ teamA: shuffled[i], teamB: shuffled[i + 1] });
          }
        }
      } else {
        setError(`Número de times não suportado: ${availableTeams.length}. Formatos suportados: 4, 8 ou 16 times.`);
        setIsDrawing(false);
        return;
      }

      const missingTeams = generatedPairings.filter(p => !p.teamA || !p.teamB);
      if (missingTeams.length > 0) {
        setError('Erro na configuração dos confrontos. Verifique a classificação dos grupos.');
        setIsDrawing(false);
        return;
      }

    } else {
      if (availableTeams.length < 2) {
        setError('São necessários pelo menos 2 times para realizar o sorteio.');
        setIsDrawing(false);
        return;
      }

      let currentTeams = [...availableTeams];
      for (let i = currentTeams.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [currentTeams[i], currentTeams[j]] = [currentTeams[j], currentTeams[i]];
      }

      for (let i = 0; i < currentTeams.length; i += 2) {
        generatedPairings.push({
          teamA: currentTeams[i],
          teamB: currentTeams[i + 1],
        });
      }
    }

    for (let i = 0; i < generatedPairings.length; i++) {
      await new Promise(res => setTimeout(res, 1500));
      setPairings(prev => {
        const updated = [...prev];
        updated[i] = generatedPairings[i];
        return updated;
      });
    }

    setIsDrawing(false);
  };

  const handleConfirmAndSave = async () => {
    if (pairings.length === 0 || pairings.some(p => !p.teamA || !p.teamB)) {
      setError('Todos os confrontos precisam estar preenchidos.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const pairingsToSend = pairings.map(p => ({
        teamA: p.teamA._id,
        teamB: p.teamB._id,
        date: new Date().toISOString().split('T')[0],
        time: '16:00',
      }));

      await championshipService.createManualKnockout(championshipId, pairingsToSend, phase);
      setSuccess('Chaveamento sorteado e salvo com sucesso!');

      setTimeout(() => {
        if (onDrawComplete) onDrawComplete();
      }, 2000);

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Falha ao salvar o chaveamento.';
      setError(errorMessage);
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPairings([]);
    setError('');
    setSuccess('');
  };

  return (
    <div className="knockout-draw-container">
      <h3>Sorteio do Mata-Mata: {phase}</h3>

      {error && <div className="status-message error">{error}</div>}
      {success && <div className="status-message success">{success}</div>}

      <div className="draw-layout">
        <div className="draw-pot">
          <h4>Times Classificados</h4>
          <div className="teams-list">
            {teams.map(team => {
              const isDrawn = pairings.some(p =>
                (p.teamA?._id === team._id) || (p.teamB?._id === team._id)
              );
              return (
                <div
                  key={team._id}
                  className={`team-item ${isDrawn ? 'drawn' : ''}`}
                  title={team.groupNameNormalized ? `Grupo ${team.groupNameNormalized} - ${team.position}º lugar` : ''}
                >
                  <img
                    src={team.badge || '/default-team-logo.png'}
                    alt={team.name}
                    onError={(e) => e.target.src = '/default-team-logo.png'}
                  />
                  <span>{team.name}</span>
                  {team.groupNameNormalized && (
                    <small className="team-position">
                      (G{team.groupNameNormalized} - {team.position}º)
                    </small>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="draw-bracket">
          <h4>Confrontos</h4>
          <div className="slots-list">
            {knockoutSlots.map((slot, index) => {
              const pair = pairings[index];
              return (
                <div key={index} className="slot-item">
                  <span className="match-number">Jogo {slot.matchNumber}</span>

                  <div className={`team-slot ${pair?.teamA ? 'filled' : ''}`}>
                    {pair?.teamA ? (
                      <>
                        <img
                          src={pair.teamA.badge || '/default-team-logo.png'}
                          alt={pair.teamA.name}
                          onError={(e) => e.target.src = '/default-team-logo.png'}
                        />
                        <span className="team-name">{pair.teamA.name}</span>
                        {pair.teamA.groupNameNormalized && (
                          <small className="team-group">G{pair.teamA.groupNameNormalized}</small>
                        )}
                      </>
                    ) : (
                      <span className="placeholder">Aguardando...</span>
                    )}
                  </div>

                  <span className="vs">VS</span>

                  <div className={`team-slot ${pair?.teamB ? 'filled' : ''}`}>
                    {pair?.teamB ? (
                      <>
                        <img
                          src={pair.teamB.badge || '/default-team-logo.png'}
                          alt={pair.teamB.name}
                          onError={(e) => e.target.src = '/default-team-logo.png'}
                        />
                        <span className="team-name">{pair.teamB.name}</span>
                        {pair.teamB.groupNameNormalized && (
                          <small className="team-group">G{pair.teamB.groupNameNormalized}</small>
                        )}
                      </>
                    ) : (
                      <span className="placeholder">Aguardando...</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="draw-controls">
        {!isDrawing && pairings.length === 0 && (
          <button
            onClick={startDraw}
            className="draw-button"
            disabled={!teams || teams.length < 2}
          >
            Iniciar Sorteio
          </button>
        )}

        {isDrawing && (
          <div className="status-message loading">
            <span className="spinner"></span>
            Sorteando confrontos...
          </div>
        )}

        {!isDrawing && pairings.length > 0 && !success && (
          <>
            <button
              onClick={handleConfirmAndSave}
              className="save-button"
              disabled={isSaving}
            >
              {isSaving ? 'Salvando...' : 'Confirmar e Salvar Chaveamento'}
            </button>

            <button
              onClick={handleReset}
              className="reset-button"
              disabled={isSaving}
            >
              Refazer Sorteio
            </button>
          </>
        )}

        <button
          onClick={onCancel}
          className="cancel-button"
          disabled={isDrawing || isSaving}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default KnockoutDrawAnimation;